import { difference, get, keyBy } from 'lodash';

/**
 * This Regular Expression returns a group that contains the parent folder path
 * and the current folder name
 *
 * Example:
 *
 *     Drivers/NiagaraNetwork/KEY2ACT_Simulated_Station/AHU_01_FPBs/FPB_01_01/
 *
 * Would return:
 *
 *     ['Drivers/NiagaraNetwork/KEY2ACT_Simulated_Station/', 'FPB_01_01']
 */
const ParentFolder = /(.+\/)(.+[^/])/;

/**
 * Expands a map into a tree data structure, optionally returning a
 * sub-tree if a `scope` ID is provided.
 *
 * Modified from https://github.com/William17/list-to-tree-lite (MIT) to
 * add a default parent ID.
 */
function mapToTree(data, options) {
    options || (options = {});
    const PARENT_KEY = options.parentKey || 'parent';
    const CHILDREN_KEY = options.childrenKey || 'children';

    const tree = [];
    const childrenOf = {};
    let parentId;
    for (let [id, item] of Object.entries(data)) {
        parentId = item[PARENT_KEY];
        if (item['dataType']) {
            item['sampleInterval'] = 15;
        }
        // every item may have children
        childrenOf[id] || (childrenOf[id] = []);
        // init its children
        item[CHILDREN_KEY] = childrenOf[id];
        if (parentId) {
            // init its parent's children object
            childrenOf[parentId] || (childrenOf[parentId] = []);
            // push it into its parent's children object
            childrenOf[parentId].push(item);
        } else {
            tree.push(item);
        }
    }

    if (options.scope) {
        return childrenOf[options.scope];
    }
    return tree;
}

/**
 * Returns all child nodes of a given node ID
 */
function getChildNodes(nodeValues, id) {
    return nodeValues
        .filter(({ parentOBIXPath }) => parentOBIXPath === id);
}

/**
 * Returns a collection of IDs, parent IDs, and child IDs for each node
 * in the provided list.
 *
 * When `autoSelectDescendants` is TRUE, the return value contains
 *
 * *  Each selected node as well as all of node IDs of its descendants
 *
 * When `autoSelectAncestors` is TRUE, the return value contains:
 *
 * *  Each selected node as well as the node ID of its ancestor
 */
function getAllSelectedIds(nodeValues, nodeMap, ids, options) {
    const ancestorIds = new Set();
    const selectedIds = new Set(ids);
    const iterator = selectedIds[Symbol.iterator]();
    let id;
    let alreadySelected = false;

    do {
        id = iterator.next().value;
        if (!id) {
            continue;
        }
        alreadySelected = options.currentSelectedIds.includes(id);
        if (alreadySelected) {
            continue;
        }
        let node = nodeMap[id];
        if (!node) {
            continue;
        }
        if (options.autoSelectDescendants) {
            if (!node.isLeaf) {
                let childNode;
                for (childNode of getChildNodes(nodeValues, id)) {
                    selectedIds.add(childNode.oBIXPath);
                }
            }
        }
        if (options.autoSelectAncestors) {
            let ancestor = node;
            do {
                ancestorIds.add(ancestor.oBIXPath);
                ancestor = nodeMap[ancestor.parentOBIXPath];
            } while (ancestor);
        }
    } while (id);
    // Merge in any selected ancestor nodes. This is not done in
    // the loop because we do not want to parse these
    ancestorIds.forEach((id) => selectedIds.add(id));
    return Array.from(selectedIds);
}

/**
 * Get a list of all node IDs to select, including (optionally) all
 * ascendent and descendent nodes, given a collection of specific IDs.
 */
export function getSelectedIds(
    nodeMap,
    ids,
    options = {}
) {
    options.currentSelectedIds || (options.currentSelectedIds = []);
    if (typeof options.autoSelectDescendants === 'undefined') {
        options.autoSelectDescendants = true;
    }
    options.autoSelectAncestors || (options.autoSelectAncestors = false);
    const nodeValues = Object.values(nodeMap);
    const idsThatWereDeselected =
        difference(options.currentSelectedIds, ids);

    const deselectedIds =
        getAllSelectedIds(
            nodeValues,
            nodeMap,
            idsThatWereDeselected,
            {
                ...options,
                currentSelectedIds:  [],
                autoSelectAncestors: true
            }
        );
    const selectedIds =
        getAllSelectedIds(nodeValues, nodeMap, ids, options);
    const idsToSelect = difference(selectedIds, deselectedIds);
    return idsToSelect;
}

/**
 * Generates a parent oBIXPath by lopping off the right most path value
 *
 * Example:
 *
 *    Drivers/NiagaraNetwork/KEY2ACT_Simulated_Station/AHU_01_FPBs/FPB_01_01/
 *
 * Becomes:
 *
 *     Drivers/NiagaraNetwork/KEY2ACT_Simulated_Station/AHU_01_FPBs/
 *
 * NOTE: The trailing slashes are important to keep as the ancestor relationship
 * is built using an exact match except for root nodes which should have a
 * NULL parentOBIXPath
 */
function generateParentOBIXPath(oBIXPath) {
    return get(oBIXPath.match(ParentFolder), '1');
}

/**
 * Normalizes and enhances all nodes in contain additional information needed by
 * the User Interface.
 */
export function buildInventoryMap(nodes) {
    return keyBy(nodes, (node) => {
        // TODO: It'd be great if parent path generation could be provided by
        // the Inventory API.
        node.parentOBIXPath = generateParentOBIXPath(node.oBIXPath);
        return node.oBIXPath;
    });
}

export function buildNiagaraTree(inventory, { rootId } = {}) {
    if (!inventory || !Object.keys(inventory).length) {
        return [];
    }
    return mapToTree(inventory, {
        childrenKey: 'items',
        parentKey:   'parentOBIXPath',
        scope:       rootId
    });
}
